import { motion } from 'motion/react';
import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '../../lib/cn';

// Six-digit code input: one box per digit, auto-advance, backspace goes back, paste fills all.
// Each box pops as it fills; the whole row shakes on error. Exposed to assistive tech as one
// labelled group with autocomplete="one-time-code" on the first box (iOS/Android autofill).

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  label: string;
  error?: boolean;
  disabled?: boolean;
  length?: number;
}

export function OtpInput({
  value,
  onChange,
  onComplete,
  label,
  error,
  disabled,
  length = 6,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const set = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    refs.current[Math.min(clean.length, length - 1)]?.focus();
  };

  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      set(value.slice(0, i - 1));
    } else if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    set(e.clipboardData.getData('text'));
  };

  return (
    <motion.div
      role="group"
      aria-label={label}
      className="flex justify-center gap-2 sm:gap-3"
      animate={error ? { x: [0, -10, 10, -6, 6, 0] } : { x: 0 }}
      transition={{ duration: 0.4 }}
    >
      {digits.map((digit, i) => (
        <motion.input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={digit}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`${label} ${i + 1}`}
          disabled={disabled}
          maxLength={length}
          onChange={(e) => set(value.slice(0, i) + e.target.value + value.slice(i + 1))}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={onPaste}
          onFocus={(e) => e.target.select()}
          animate={digit ? { scale: [1, 1.12, 1] } : { scale: 1 }}
          transition={{ duration: 0.25 }}
          className={cn(
            'tabular h-14 w-11 rounded-md border-2 bg-surface text-center font-display text-2xl font-bold text-text outline-none transition-colors sm:h-16 sm:w-13',
            error ? 'border-danger' : digit ? 'border-primary' : 'border-border',
            'focus:border-ring focus:shadow-[0_0_0_4px_color-mix(in_oklab,var(--ring)_22%,transparent)]',
          )}
        />
      ))}
    </motion.div>
  );
}
